"use client";

import { MessageCircleQuestionMark as MessageCircleQuestion } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How do diagnostic sessions work?",
  answer: "We begin with a 30-minute diagnostic assessment to map your student's current knowledge gaps. We then build a custom curriculum that targets specific weaknesses in Calculus, Physics, or SAT Math, ensuring every minute is spent on high-impact growth."
}, {
  question: "What is the investment?",
  answer: "Our tutoring packages are structured for long-term academic mastery. We offer flexible scheduling options and tiered pricing based on the intensity of the subject matter. We will outline a clear roadmap during your initial consultation."
}, {
  question: "What technology is required?",
  answer: "All you need is a reliable internet connection and a tablet with a stylus. We utilize a professional-grade digital whiteboard that allows for real-time collaborative problem solving, mirroring the experience of an in-person session."
}, {
  question: "Are your tutors subject-matter experts?",
  answer: "Every ApexTutor instructor is a vetted STEM professional or top-tier university graduate. They undergo rigorous pedagogical training to ensure they can explain complex concepts with absolute clarity and precision."
}, {
  question: "Can we adjust our schedule?",
  answer: "We understand the demands of a rigorous academic calendar. You can manage your sessions through our portal, allowing for rescheduling or adjustments to accommodate exam weeks, extracurriculars, or seasonal breaks."
}, {
  question: "Do you support specific exam boards?",
  answer: "Yes, we specialize in AP, IB, and SAT/ACT preparation. Whether you are tackling AP Physics C or SAT Math Level 2, our strategies are aligned with the latest testing standards and scoring rubrics."
}, {
  question: "What is the ideal age range?",
  answer: "ApexTutor is designed for high-achieving students in grades 9 through 12. Our methodology is specifically calibrated for the transition from high school to elite university-level STEM coursework."
}, {
  question: "How do I manage my account?",
  answer: "Your parent dashboard provides full transparency into your student's progress, upcoming sessions, and billing. You can update your preferences or contact your tutor directly with just a few clicks."
}];
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1315" id="common-questions" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12">
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-[120px]">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  § ACADEMIC DATA · 02 / FREQUENT INQUIRIES
                </span>
                <span className="inline-block w-2 h-2 rounded-lg" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>

              {}
              <h2 className="font-default font-semibold text-4xl md:text-5xl leading-[1.08] tracking-tight text-foreground mb-6">
                <span>Answers for </span>
                <span className="relative inline-block italic font-serif">
                  <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" preserveAspectRatio="none" fill="none" aria-hidden="true">
                    <path d="M 8 30 Q 4 14, 30 8 Q 60 2, 95 6 Q 116 12, 114 32 Q 110 52, 78 54 Q 40 58, 18 52 Q 2 46, 8 30 Z" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 400,
                    strokeDashoffset: isVisible ? 0 : 400,
                    transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms",
                    transform: "rotate(-4deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative">ambitious</span>
                </span>
                <span> families.</span>
              </h2>

              <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                Clarity is the first step toward mastery. We have compiled the most common questions to help you understand how ApexTutor builds academic excellence.
              </p>

              {}
              <Link to="/ResourcesPage" className="group inline-flex items-center gap-2 font-default font-medium text-foreground">
                <span className="relative">
                  View full resource library
                  <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 group-hover:w-full transition-all duration-300 ease-out" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1" style={{
                color: "var(--primary)"
              }}>
                  →
                </span>
              </Link>

              {}
              <div className="mt-12 hidden lg:block">
                <svg viewBox="0 0 180 220" className="w-44 h-auto" aria-hidden="true">
                  {}
                  <line x1="90" y1="30" x2="90" y2="55" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="90" cy="22" r="8" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="3">
                    <animate attributeName="opacity" values="1;0.6;1" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  {}
                  <rect x="40" y="55" width="100" height="90" rx="0" fill="var(--chart-2)" stroke="var(--foreground)" strokeWidth="3" />
                  {}
                  <circle cx="68" cy="92" r="5" fill="var(--foreground)" />
                  <circle cx="112" cy="92" r="5" fill="var(--foreground)" />
                  {}
                  <circle cx="58" cy="108" r="6" fill="var(--primary)" opacity="0.7" />
                  <circle cx="122" cy="108" r="6" fill="var(--primary)" opacity="0.7" />
                  {}
                  <path d="M 80 120 Q 90 116, 100 120" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" fill="none" />
                  {}
                  <rect x="55" y="145" width="70" height="55" rx="0" fill="var(--chart-2)" stroke="var(--foreground)" strokeWidth="3" />
                  {}
                  <path d="M 140 130 Q 155 120, 148 100 Q 142 85, 125 95" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" fill="var(--chart-2)" />
                  {}
                  <circle cx="155" cy="55" r="14" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="2.5" />
                  <circle cx="140" cy="72" r="5" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="2" />
                </svg>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="flex flex-col gap-2">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 rounded-lg border border-border bg-card text-card-foreground shadow-md transition-all duration-300 hover:shadow-lg data-[state=open]:border-primary/40 px-2">
                  <AccordionTrigger className="group hover:no-underline py-6 px-4 md:px-6 [&>svg]:hidden">
                    <div className="flex items-center gap-4 md:gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground shrink-0 w-12">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-semibold text-lg md:text-xl text-foreground leading-snug flex-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-lg border-2 border-foreground transition-all duration-300 group-data-[state=open]:rotate-45 group-data-[state=open]:bg-primary group-data-[state=open]:border-primary group-data-[state=open]:text-primary-foreground" style={{
                    backgroundColor: "var(--chart-3)",
                    color: "var(--foreground)"
                  }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 2 V14 M2 8 H14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 md:px-6 pb-6">
                    <div className="pl-0 md:pl-[4.5rem]">
                      <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                      <Link to="/TheApexMethodPage" className="group/link mt-4 inline-flex items-center gap-2 font-default font-medium text-sm text-foreground">
                        <MessageCircleQuestion className="w-4 h-4" style={{
                      color: "var(--primary)"
                    }} />
                        <span className="relative">
                          Learn about our methodology
                          <span className="absolute left-0 -bottom-0.5 h-[1.5px] w-0 group-hover/link:w-full transition-all duration-300 ease-out" style={{
                        backgroundColor: "var(--primary)"
                      }} />
                        </span>
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}